import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { validatePassword } from "@/lib/password-validation";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { Eye, EyeOff } from "lucide-react";
import SEOHead from "@/components/SEOHead";

const HCAPTCHA_SITE_KEY = "3e5457b6-3fe6-42a0-89b2-955edd8447ae";

const Login = () => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailMode, setEmailMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const captchaRef = useRef<HCaptcha>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const justConfirmed = searchParams.get("confirmed") === "true";

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      const msg = error.message?.toLowerCase() ?? "";
      if (msg.includes("invalid") || msg.includes("credentials")) {
        toast.error("That email and password didn't match — try again.");
      } else if (msg.includes("confirm")) {
        toast.error("Please confirm your email first — check your inbox.");
      } else {
        toast.error("That didn't work — give it another go.");
      }
    } else {
      navigate("/dashboard");
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaToken) {
      toast.error("Please complete the verification above.");
      return;
    }
    const { valid, error: validationError } = validatePassword(password);
    if (!valid) {
      toast.error(validationError!);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login?confirmed=true`,
        captchaToken,
      },
    });
    setLoading(false);
    captchaRef.current?.resetCaptcha();
    setCaptchaToken(null);
    if (error) {
      toast.error("That didn't work — give it another go.");
    } else if (data.user?.identities?.length === 0) {
      toast.error("An account with this email may already exist. Try signing in instead.");
    } else {
      toast.success("Check your email to confirm your account.");
      setEmailMode("signin");
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 animate-fade-in">
      <div className="w-full max-w-sm">
        {justConfirmed && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-2xl bg-[#FAF7F2] border border-[#C9A84C]/30 px-5 py-4 text-center"
          >
            <p className="text-[#C9A84C] text-xs font-medium uppercase tracking-widest mb-1">
              You're confirmed
            </p>
            <p className="font-serif text-[#1A1A1A] text-lg">
              Welcome to Dearestly.
            </p>
            <p className="text-[#666666] text-sm mt-1">
              Sign in to get started.
            </p>
          </motion.div>
        )}

        {/* Logo + tagline */}
        <div className="mb-10 text-center">
          <SEOHead title="Log in — Dearestly." description="Log in to Dearestly., your relationship memory. Remember every person who matters." path="/login" noindex />
          <h1 className="font-serif text-4xl font-bold text-foreground">Dearestly.</h1>
          <div className="mx-auto mt-1 h-0.5 w-12 rounded-full bg-accent" />
          <p className="mt-2 text-sm text-muted-foreground">Remember everyone who matters.</p>
        </div>

        {/* OAuth buttons */}
        <div className="space-y-3">
          {/* Apple */}
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2.5 rounded-full bg-[hsl(0_0%_0%)] px-6 py-3 text-base font-medium text-white transition-opacity hover:opacity-90"
            onClick={async () => {
              const { error } = await lovable.auth.signInWithOAuth("apple", {
                redirect_uri: window.location.origin,
              });
              if (error) toast.error("Can't reach Dearestly right now. Check your connection and try again.");
            }}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="white">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            Continue with Apple
          </button>

          {/* Google */}
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2.5 rounded-full border border-border bg-card px-6 py-3 text-base font-medium text-foreground transition-colors hover:bg-muted"
            onClick={async () => {
              const { error } = await lovable.auth.signInWithOAuth("google", {
                redirect_uri: window.location.origin,
              });
              if (error) toast.error("Can't reach Dearestly right now. Check your connection and try again.");
            }}
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>
        </div>

        {/* Divider */}
        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-sm text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Email link / form */}
        {!showEmailForm ? (
          <button
            type="button"
            onClick={() => setShowEmailForm(true)}
            className="mx-auto block text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Continue with email →
          </button>
        ) : (
          <div className="animate-fade-in space-y-4">
            {/* Mode toggle */}
            <div className="flex justify-center gap-4 text-sm">
              <button
                type="button"
                onClick={() => setEmailMode("signin")}
                className={`pb-0.5 transition-colors ${emailMode === "signin" ? "font-semibold text-foreground border-b-2 border-accent" : "text-muted-foreground hover:text-foreground"}`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => setEmailMode("signup")}
                className={`pb-0.5 transition-colors ${emailMode === "signup" ? "font-semibold text-foreground border-b-2 border-accent" : "text-muted-foreground hover:text-foreground"}`}
              >
                Create account
              </button>
            </div>

            <form onSubmit={emailMode === "signin" ? handleSignIn : handleSignUp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={emailMode === "signup" ? "At least 8 characters" : "Your password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={emailMode === "signup" ? 8 : undefined}
                    className="rounded-full pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {emailMode === "signup" && (
                <>
                  <div className="flex justify-center">
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={HCAPTCHA_SITE_KEY}
                      onVerify={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                    />
                  </div>
                  {!captchaToken && (
                    <p className="text-center text-xs text-muted-foreground">
                      Please complete the verification above.
                    </p>
                  )}
                </>
              )}

              <Button
                type="submit"
                disabled={loading || (emailMode === "signup" && !captchaToken)}
                className="w-full rounded-full"
                size="lg"
              >
                {loading
                  ? emailMode === "signin" ? "Signing in…" : "Creating account…"
                  : emailMode === "signin" ? "Sign In" : "Get Started"}
              </Button>
            </form>

            {emailMode === "signin" && (
              <p className="text-center">
                <Link to="/forgot-password" className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
                  Forgot password?
                </Link>
              </p>
            )}
          </div>
        )}

        {/* Legal text */}
        <p className="mt-10 text-center text-xs text-muted-foreground/70 px-4">
          By continuing, you agree to Dearestly's{" "}
          <Link to="/terms" className="underline underline-offset-4 hover:text-foreground">Terms of Use</Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline underline-offset-4 hover:text-foreground">Privacy Notice</Link>.
        </p>
      </div>
    </div>
  );
};

export default Login;
